import React from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS_ADMIN_PAGE, COLORS_CONFIG_PAGE, StyledSidebar } from '../../conf/constants';
import { Voucher } from '../types/voucher.types';

interface VoucherSidebarProps {
  sidebarWidth: string;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  vouchers: Voucher[];
  loading: boolean;
  selectedVoucher: number;
  onVoucherSelect: (voucher: Voucher) => void;
  onCreateClick: () => void;
  onToggleStatus: () => void;
  currentVoucher?: Voucher;
  isToggling: boolean;
}

const VoucherSidebar: React.FC<VoucherSidebarProps> = ({
  sidebarWidth,
  searchTerm,
  onSearchTermChange,
  vouchers,
  loading,
  selectedVoucher,
  onVoucherSelect,
  onCreateClick,
  onToggleStatus,
  currentVoucher,
  isToggling
}) => {
  return (
    <StyledSidebar elevation={0} sidebarWidth={sidebarWidth}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Vouchers
          </Typography>
          <TextField
            size="small"
            placeholder="Search Vouchers..."
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              bgcolor: COLORS_CONFIG_PAGE.background,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
              },
              borderRadius: 3,
              p: 1,
            }}
          />
        </Box>

        {/* Voucher List */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <Typography variant="body2" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>
                Loading vouchers...
              </Typography>
            </Box>
          ) : vouchers.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }}>
                No vouchers found
              </Typography>
            </Box>
          ) : (
            vouchers.map((voucher) => (
              <Box
                key={voucher.voucherId}
                onClick={() => onVoucherSelect(voucher)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.5,
                  cursor: 'pointer',
                  bgcolor: selectedVoucher === voucher.voucherId
                    ? COLORS_ADMIN_PAGE.PRIMARY
                    : COLORS_ADMIN_PAGE.LIGHT_GRAY,
                  color: selectedVoucher === voucher.voucherId
                    ? COLORS_ADMIN_PAGE.WHITE
                    : COLORS_ADMIN_PAGE.BLACK,
                  mb: 1,
                  borderRadius: '4px',
                  opacity: voucher.isActive ? 1 : 0.5,
                  '&:hover': {
                    bgcolor: selectedVoucher === voucher.voucherId
                      ? COLORS_ADMIN_PAGE.PRIMARY
                      : COLORS_ADMIN_PAGE.BACKGROUND,
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, py: 2 }}>
                    {voucher.code}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Bottom Buttons */}
        <Box sx={{
          p: 2,
          borderTop: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateClick}
            sx={{
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY,
              '&:hover': { bgcolor: COLORS_ADMIN_PAGE.PRIMARY }
            }}
          >
            NEW
          </Button>
          <Button
            variant="contained"
            startIcon={currentVoucher?.isActive ? <DeleteIcon /> : <AddIcon />}
            disabled={!selectedVoucher || isToggling}
            onClick={onToggleStatus}
            sx={{
              bgcolor: currentVoucher?.isActive ? 'error.main' : 'success.main',
              '&:hover': {
                bgcolor: currentVoucher?.isActive ? 'error.dark' : 'success.dark'
              },
              '&:disabled': {
                bgcolor: COLORS_ADMIN_PAGE.BORDER,
                color: COLORS_ADMIN_PAGE.TEXT_SECONDARY
              }
            }}
          >
            {isToggling ? 'UPDATING...' : (currentVoucher?.isActive ? 'DISABLE' : 'ACTIVATE')}
          </Button>
        </Box>
      </Box>
    </StyledSidebar>
  );
};

export default VoucherSidebar;